/**
 * Revision History
 * 
 * 12/27/2021 ptkim       Add compareIso function
 * 09/19/2022 ptkim       moment.format('YYYY-MM-DDTHH:mm:ss'), remove tailingZ
 */
import _ from 'lodash';
import moment from 'moment';

import {getDateFormat, getDateTimeFormat} from '../globals';

function formatIso(iso:string|null|undefined, format: string|null|undefined) {
  if (!iso) {
    return '';
  }

  if (!format) {
    return '';
  }

  return moment(iso).format(format);
}

function formatIsoUtc(iso:string|null|undefined, format: string|null|undefined) {
  if (!iso) {
    return '';
  }

  if (!format) {
    return '';
  }

  return moment.utc(iso).format(format);
}


/**
 * Formats iso date in format 'JUNE 06, 2022'
 *
 * @export
 * @param {string} iso
 * @return {*} 
 */
export function getLongDateFromIso(iso: string) {
  return moment(iso).format('MMMM DD, YYYY');
}

/**
 * getCurrentDateIso() - get's current time (local time) in ISO 8601
 *
 * @export
 * @return {*} 
 */
export function getCurrentDateIso(dateOnly: boolean = false) {
  return moment().format(dateOnly ? 'YYYY-MM-DDT00:00:00' : 'YYYY-MM-DDTHH:mm:ss');
}


/**
 * formatDate - formats iso date with current system settings date format
 *
 * @export
 * @param {(string|null|undefined)} isoDate
 * @return {*}  {string}
 */
export function formatDate(isoDate: string|null|undefined, formatString?: string): string {
  return formatIso(isoDate, formatString || getDateFormat());
}

/**
 * formatDateTime - formats iso date with current system settings date time format
 *
 * @export
 * @param {(string|null|undefined)} isoDate
 * @return {*}  {string}
 */
export function formatDateTime(isoDateTime: string | null | undefined, format?: string, includeSec?: boolean, forList?:boolean): string {
  if(forList) format = format + ' HH:mm'
  const format2 = includeSec ? `${format || getDateTimeFormat()}:ss` : `${format || getDateTimeFormat()}`;
  return formatIso(isoDateTime, format || format2);
}


/**
 * formatDateTimeFull - formats iso date with current system settings date time format with AM/PM suffix
 *
 * @export
 * @param {(string|null|undefined)} isoDate
 * @return {*}  {string}
 */
 export function formatDateTimeFull(isoDateTime: string|null|undefined, format?: string): string {
  const appliedFormat = `${format || getDateTimeFormat()} A`.replace('HH', 'hh');
  return formatIso(isoDateTime, appliedFormat);
}

/**
 * isoToDate - converts iso string to javascript native Date object
 *
 * @export
 * @param {(string|null)} iso
 * @return {*}  {(Date|null)}
 */
export function isoToDate(iso: string|null): Date|null {
  if (!iso) {
    return null;
  }
const ewr = moment(iso).toDate();
  return moment(iso).toDate();
}

/**
 * now - get current timestamp as iso
 *
 * @export
 * @return {*} 
 */
export function now() {
  return moment().format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * now - get current timestamp as iso
 *
 * @export
 * @return {*}
 */

export function nowMillisecond() {
  return moment().format('YYYY-MM-DDTHH:mm:ss.SSS');
}

/**
 * now - get current timestamp as iso
 *
 * @export
 * @return {*} 
 */
 export function nowDate() {
  return moment().format('YYYY-MM-DDT00:00:00');
}

/**
 * now - get current timestamp as iso
 *
 * @export
 * @return {*}
 */
export function getCurrentMonth() {
  return moment().month();
}

/**
 * now - get current timestamp as iso
 *
 * @export
 * @return {*}
 */
export function getCurrentYear() {
  return moment().year();
}

/**
 * now - get current utc timestamp as iso
 *
 * @export
 * @return {*} 
 */
 export function nowUtc() {
  return moment().utc().format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * nowStartOfDay - get current timestamp - start of day as iso
 *
 * @export
 * @return {*} 
 */
 export function nowStartOfDay() {
  return moment().startOf('day').format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * nowStartOfMonth - get current timestamp - start of month as iso
 *
 * @export
 * @return {*}
 */
export function nowStartOfMonth() {
  return moment().startOf('month').format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * prevMonthStartOfDay - get current timestamp - previous month start of day as iso
 *
 * @export
 * @return {*}
 */
export function prevMonthStartOfDay() {
  return moment().startOf('month').add(-1, 'months').format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * prevMonthStartOfDay - get current timestamp - previous month last of day as iso
 *
 * @export
 * @return {*}
 */
export function prevMonthLastOfDay() {
  return moment().startOf('month').add(-1, 'days').format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * Get difference in months between 2 iso dates in months
 *
 * @export
 * @param {string} iso1
 * @param {string} iso2
 * @return {*}  {number}
 */
export function getDiffMonths(iso1:string, iso2: string): number {
  return moment(iso1).diff(moment(iso2), 'months')
}

/**
 * Get difference in months between 2 iso dates in days
 *
 * @export
 * @param {string} iso1
 * @param {string} iso2
 * @return {*}  {number}
 */
 export function getDiffDays(iso1:string, iso2: string): number {
  return moment(iso1).diff(moment(iso2), 'days')
}

export interface DateTextFormatResult {
  valid: boolean,
  iso: string|null,
};

/**
 * dateTextToIso - converts date string to iso. Uses current date format from settings
 *
 * @export
 * @param {string} date
 */
export function dateTextToIso(date:string, formatString?: string):DateTextFormatResult {
  const format = formatString || getDateFormat();
  if (!format) {
    return { valid: false, iso: null };
  }

  const strictMode = true;

  const m = moment(date, format, strictMode);
  //const valid = m.isValid() && !(compareIso(date, '1980') < 0 || compareIso(date, '2079') >= 0);
  const valid = m.isValid() && !(m.year() < 1980 || m.year() > 2079);

  //<save-sqlinfo: Debug result includes timezone offset:
  //return { valid, iso: valid ? m.format() : null };
  let iso = valid ? m.format() : null;
  if(iso) {
    // Append 00:00:00 after T
    iso = iso.substring(0, 11) + "00:00:00";
  }
  return { valid, iso};
}

/**
 * dateTimeTextToIso - converts date time string to iso. Uses current date format from settings
 *
 * @export
 * @param {string} date
 */
 export function dateTimeTextToIso(dateTime:string, formatString?: string):DateTextFormatResult {
  //debugger;
  const format = formatString || getDateTimeFormat();
  if (!format) {
    return { valid: false, iso: null };
  }

  const strictMode = true;

  const m = moment(dateTime, format, strictMode);
  const valid = m.isValid();

  let iso = valid ? m.format() : null;
  return { valid, iso};
}

/**
 * jsDateToIso - converts javascript native date to iso.
 *
 * @export
 * @param {Date} date
 * @return {*}  {string}
 */
export function jsDateToIso(date: Date): string {
  return moment(date).format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * getTime - extracts time in HH:mm format from iso, if available. Time zone is ignored for consistency resons
 *
 * @export
 * @param {(string|null|undefined)} iso
 * @return {*}  {(string|null)}
 */
export function getTime(iso: string|null|undefined): string|null {
  if (!iso) {
    return null;
  }

  if (!moment(iso).isValid()) {
    return null;
  }

  const tIndex = iso.indexOf('T');
  if (tIndex < 0) {
    return null; // Should never happen
  }

  const hhMM = 'HH:mm';

  if (tIndex + hhMM.length >= iso.length) {
    return null; // Should never happen
  }

  return iso.substring(tIndex + 1, tIndex + hhMM.length + 1);
}

/**
 * pt211230: Get date part only with T00:00:00 into iso format
 * @param iso 
 * @returns 
 */
export function getDate(iso: string|null|undefined): string|null {
  if (!iso) {
    return null;
  }

  if (!moment(iso).isValid()) {
    return null;
  }

  const tIndex = iso.indexOf('T');
  if (tIndex < 0) {
    return null; // Should never happen
  }

  return iso.substring(0, tIndex) + 'T00:00:00';
}

/**
 * addMilliseconds - Adds number of milliseconds to iso date and returns a new date as iso
 *
 * @export
 * @param {string} iso1
 * @param {string} iso2
 * @return {*}  {number}
 */
 export function addMilliseconds(iso:string, milliseconds:number): string {
  return moment(iso).add(milliseconds, 'milliseconds').format();
}

/**
 * addDays - Adds number of days to iso date and returns a new date as iso
 *
 * @export
 * @param {string} iso1
 * @param {string} iso2
 * @return {*}  {number}
 */
 export function addDays(iso:string, days:number): string {
  return moment(iso).add(days, 'days').format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * addMonth - Adds number of months to iso date and returns a new date as iso
 *
 * @export
 * @return {*}  {number}
 * @param iso
 * @param days
 */
export function addMonths(iso:string, months:number): string {
  return moment(iso).add(months, 'months').format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * addMonth - Adds number of years to iso date and returns a new date as iso
 *
 * @export
 * @return {*}  {number}
 * @param iso
 * @param years
 */
export function addYears(iso:string, years:number): string {
  return moment(iso).add(years, 'years').format('YYYY-MM-DDTHH:mm:ss');
}

/**
 * isIsoBefore - check if isBeforeCheck is before iso
 *
 * @export
 * @param {string} iso
 * @param {string} isoBeforeCheck
 * @return {*}  {boolean}
 */
export function isIsoBefore(iso:string, isoBeforeCheck:string): boolean {
  return moment(iso).isBefore(moment(isoBeforeCheck));
}

/**
 * Compare two ISO date string
 * 
 * @param iso1 ISO date string 1
 * @param iso2 ISO date string 2
 * @returns 0 (same), > 0 (iso1 > iso2), < 0 (iso1 < iso2)
 */
 export function compareIso(iso1:string, iso2:string):number {
  const m1 = moment(iso1);
  const m2 = moment(iso2);
  if(m1.isBefore(m2)) return -1;
  else if(m1.isSame(m2)) return 0;
  else return 1;
}

/**
 * Reutrn bigger iso date string
 * @param iso1 
 * @param iso2 
 */
export function getBigDate(iso1:string|null, iso2:string|null):string|null {
  if(!iso1) {
    if(!iso2) return null;
    else return iso2;
  }
  else if(!iso2)
    return iso1;
  else {
    if(isIsoBefore(iso1, iso2))
      return iso2;
    else
      return iso1;
  }
}

/**
 * Dario: Set time part to iso. If time is null it sets it as 00:00
 * Time is expected to be in correct format (HH:mm)
 * @param iso, time.
 * @returns 
 */
 export function setTime(iso: string, time: string|null): string {
  if (!moment(iso).isValid()) {
    throw new Error('Invalid iso: ' + iso);
  }

  const tIndex = iso.indexOf('T');
  if (tIndex < 0) {
    throw new Error('Invalid iso: ' + iso);
  }

  const timePart = time || '00:00';

  return `${iso.substring(0, tIndex)}T${timePart}`;
}

/**
 * Converts unix epoch number to date time string. Currently used to get 'logged at' time which
 * is parsed from jwt
 * 
 * @param epoch 
 * @returns 
 */
export function getDateFromEpoch(epoch: number) {
  return moment(new Date(epoch * 1000)).format();
}

/**
 * Converts iso date to unix epoch number
 * 
 * @param epoch 
 * @returns 
 */
 export function getEpochFromDate(iso: string) {
  return moment(iso).valueOf();
}

/**
 * pt220220
 * To check if any value is ISO8601 datetime string
 * @param iso 
 * @returns 
 */
export function isIsoDateTime(iso:any) {
  // return (_.isString(iso) && moment(iso, "YYYY-MM-DDTHH:mm:ss", false).isValid());

  // Implementation note:
  //  Because how moment.js is implemented, the following return true:
  //
  //  moment('2022-02-02t12:00:01xx', "YYYY-MM-DDTHH:mm:ss", false).isValid()
  //
  // To have more precise implementation, let's use strict mode parsing:
  //                                      (Dario)
  return (_.isString(iso) && moment(iso, moment.ISO_8601, true).isValid());
}

export const monthDaysList = Array.from({length: 31}, (_, i) => ({value: (i + 1).toString(), label: (i + 1).toString()}))
export const monthsList = Array.from({length: 12}, (_, i) => ({value: (i + 1).toString(), label: (i + 1).toString()}))
export const yearsList = Array.from({length: 30}, (_, i) => ({value: (i + 1).toString(), label: (i + 2001).toString()}))

export const quartersList = Array.from({length: 4}, (_, i) => ({value: (i + 1).toString(), label: (i + 1).toString()}))
export const halfList = Array.from({length: 2}, (_, i) => ({value: (i + 1).toString(), label: (i + 1).toString()}))

/**
 * formatDateUtc - formats iso date with current system settings date format
 *
 * @export
 * @param {(string|null|undefined)} isoDate
 * @return {*}  {string}
 */
export function formatDateUtc(isoDate: string|null|undefined, formatString?: string): string {
  return formatIsoUtc(isoDate, formatString || getDateFormat());
}
